import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, Marker, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { DeliveryPolygon } from '../../types/deliveryPolygons';
import { deliveryPolygonsService } from '../../services/deliveryPolygonsService';

interface PolygonTesterProps {
  polygons: DeliveryPolygon[];
  height?: string;
}

const mapContainerStyle = {
  width: '100%',
};

const defaultCenter = {
  lat: 37.2242,
  lng: 67.2783,
};

const PolygonTester: React.FC<PolygonTesterProps> = ({ polygons, height = '400px' }) => {
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [detectedPolygon, setDetectedPolygon] = useState<DeliveryPolygon | null>(null);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMapClick = useCallback(
    async (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setSelectedLocation({ lat, lng });
        setTesting(true);
        setError('');
        setDetectedPolygon(null);

        try {
          const response = await deliveryPolygonsService.findPolygonByCoordinates(lat, lng);
          if (response.success && response.data) {
            setDetectedPolygon(response.data);
          } else {
            setError(t('polygonTester.noPolygonFound'));
          }
        } catch (err) {
          setError(t('polygonTester.errorTesting'));
        } finally{
          setTesting(false);
        }
      }
    },
    []
  );

  const handleClear = () => {
    setSelectedLocation(null);
    setDetectedPolygon(null);
    setError('');
  };

  if (!isLoaded) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>{t('polygonTester.loadingMap')}</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginBottom: '0.5rem' }}>{t('polygonTester.title')}</h3>
        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
          {t('polygonTester.subtitle')}
        </p>
        {selectedLocation && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {t('polygonTester.clearTest')}
          </button>
        )}
      </div>

      <GoogleMap
        mapContainerStyle={{ ...mapContainerStyle, height }}
        center={defaultCenter}
        zoom={13}
        onLoad={onLoad}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {polygons.map((polygon, index) => {
          const isDetected = detectedPolygon?.id === polygon.id;
          const path = polygon.coordinates.map((coord) => ({
            lat: coord.latitude,
            lng: coord.longitude,
          }));

          // Generate distinct colors for each polygon
          const colors = ['#2196F3', '#9C27B0', '#FF9800', '#E91E63', '#00BCD4', '#FFC107', '#673AB7'];
          const baseColor = colors[index % colors.length];

          return (
            <Polygon
              key={polygon.id}
              paths={path}
              options={{
                fillColor: isDetected ? '#28a745' : baseColor,
                fillOpacity: isDetected ? 0.5 : 0.2,
                strokeColor: isDetected ? '#28a745' : baseColor,
                strokeOpacity: isDetected ? 1 : 0.6,
                strokeWeight: isDetected ? 3 : 2,
                clickable: false,
              }}
            />
          );
        })}

        {selectedLocation && (
          <Marker
            position={selectedLocation}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: detectedPolygon ? '#28a745' : '#dc3545',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}
      </GoogleMap>

      {testing && (
        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
          {t('polygonTester.testing')}
        </div>
      )}

      {selectedLocation && !testing && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>{t('polygonTester.selectedLocation')}</strong>
            </div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              {t('polygonTester.latitude', { value: selectedLocation.lat.toFixed(6) })}
            </div>
            <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
              {t('polygonTester.longitude', { value: selectedLocation.lng.toFixed(6) })}
            </div>

            {detectedPolygon ? (
              <div
                style={{
                  padding: '1rem',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  border: '1px solid #c3e6cb',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{t('polygonTester.polygonFound')}</div>
                <div style={{ fontSize: '1rem', marginBottom: '0.25rem' }}><strong>{detectedPolygon.title}</strong></div>
                <div style={{ fontSize: '0.9rem' }}>{t('polygonTester.polygonId', { id: detectedPolygon.id })}</div>
                <div style={{ fontSize: '0.9rem' }}>{t('polygonTester.deliveryPrice', { price: detectedPolygon.price.toLocaleString() })}</div>
              </div>
            ) : error ? (
              <div
                style={{
                  padding: '1rem',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  border: '1px solid #f5c6cb',
                  borderRadius: '4px',
                }}
              >
                {error}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolygonTester;
